import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Collapse, IconButton, Tooltip } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Team } from '../../models/types';
import { SuspiciousCountBadge } from './SuspiciousCountBadge';

interface TeamStatsBarRowProps {
  teamId: string;
  teamName: string;
  memberCount: number;
  rank: number;
  // Admin only: flagged activities in this team awaiting review.
  suspiciousCount?: number;
  valueLabel: string;
  widthPercent: number;
  highlight: boolean;
  // Full team record, used for member names and adding members; absent if the leaderboard has a team the list doesn't.
  team?: Team;
  membersLocked: boolean;
  onAddMember: (team: Team) => void;
}

const memberDisplayName = (member: Team['members'][number]): string => {
  // Prefer the full Strava name, then name, then username.
  const strava = member.stravaProfile;
  if (strava?.firstname && strava?.lastname) return `${strava.firstname} ${strava.lastname}`;
  return member.name || member.username || member.userId;
};

export const TeamStatsBarRow: React.FC<TeamStatsBarRowProps> = ({ teamId, teamName, memberCount, rank, suspiciousCount, valueLabel, widthPercent, highlight, team, membersLocked, onAddMember }) => {
  const { t } = useTranslation('contests');
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const members = team?.members ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, fontSize: 14 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', fontWeight: 500, minWidth: 0 }}>
          <Box component="span" sx={{ color: 'var(--muted)', fontVariantNumeric: 'tabular-nums', width: 18, flex: 'none' }}>
            {rank}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => navigate(`/teams/${teamId}`)}
            sx={{ all: 'unset', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' }, '&:focus-visible': { outline: '2px solid var(--primary)' } }}
          >
            {teamName}
          </Box>
          <SuspiciousCountBadge count={suspiciousCount} />
          <Box
            component="button"
            type="button"
            onClick={() => setExpanded(open => !open)}
            disabled={members.length === 0}
            aria-expanded={expanded}
            aria-label={expanded ? t('teamStats.hideMembers', { team: teamName }) : t('teamStats.showMembers', { team: teamName })}
            sx={{ all: 'unset', display: 'inline-flex', alignItems: 'center', fontSize: 12, color: 'var(--muted)', fontWeight: 400, whiteSpace: 'nowrap', cursor: members.length ? 'pointer' : 'default', '&:hover': { color: members.length ? 'var(--fg)' : undefined }, '&:focus-visible': { outline: '2px solid var(--primary)' } }}
          >
            {t('teamStats.memberCount', { count: memberCount })}
            {members.length > 0 && <ExpandMoreIcon sx={{ fontSize: 16, transition: 'transform .15s', transform: expanded ? 'rotate(180deg)' : 'none' }} />}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 'none' }}>
          <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {valueLabel}
          </Box>
          {team && (
            <Tooltip title={membersLocked ? t('teamStats.membersLocked') : t('memberForm.addOne')}>
              <span>
                <IconButton aria-label={t('memberForm.title', { team: teamName })} onClick={() => onAddMember(team)} disabled={membersLocked} size="small" sx={{ color: 'var(--primary)' }}>
                  <PersonAddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ height: 10, borderRadius: '99px', backgroundColor: 'var(--muted-bg)', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', borderRadius: '99px', width: `${widthPercent}%`, background: highlight ? 'var(--grad)' : 'var(--primary)' }} />
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pl: '26px' }}>
          {members.map(member => (
            <Box key={member.userId} component="span" sx={{ fontSize: 12, border: '1px solid var(--border)', borderRadius: '6px', px: 1, py: '2px' }}>
              {memberDisplayName(member)}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
